import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import {
  listPermissionCategories,
  syncPermissionCategoriesFromApi,
} from "@/lib/admin/permissionCategoriesStore";
import {
  createManagedPermission,
  deleteManagedPermission,
  listManagedPermissions,
  syncManagedPermissionsFromApi,
  updateManagedPermission,
  type ManagedPermissionDraft,
  type ManagedPermissionRecord,
} from "@/lib/admin/managedPermissionsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { isAxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import { CalendarIcon, Edit, KeyRound, LayoutGrid, List, Loader2, Plus, Search, Shield, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type SortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "updated_latest" | "updated_oldest";

function errorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

const emptyDraft = (): ManagedPermissionDraft => ({
  name: "",
  title: "",
  permissionCategoryId: "",
});

const Permissions = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "managed_permission_can_create");
  const canUpdate = can(user, "managed_permission_can_update");
  const canDelete = can(user, "managed_permission_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  const [categoryVersion, setCategoryVersion] = useState(0);
  const refreshCategories = useCallback(() => setCategoryVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("name_asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ManagedPermissionRecord | null>(null);
  const [draft, setDraft] = useState<ManagedPermissionDraft>(emptyDraft);
  const [deleteTarget, setDeleteTarget] = useState<ManagedPermissionRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const rows = useMemo(() => listManagedPermissions(), [version]);
  const categories = useMemo(() => listPermissionCategories(), [categoryVersion, version]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await Promise.all([syncPermissionCategoriesFromApi(), syncManagedPermissionsFromApi()]);
        if (mounted) {
          refreshCategories();
          refresh();
        }
      } catch (error) {
        console.error("[Permissions] Load failed", error);
        if (isAxiosError(error) && error.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        if (mounted) toast.error(errorMessage(error));
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh, refreshCategories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        (r.categoryName && r.categoryName.toLowerCase().includes(q))
      );
    });
  }, [rows, query]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const paginationKey = `${query}|${sortBy}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (r: ManagedPermissionRecord) => {
    setEditing(r);
    setDraft({
      name: r.name,
      title: r.title,
      permissionCategoryId: r.permissionCategoryId ?? "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const save = async () => {
    if (isSaving) return;
    if (!draft.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!draft.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (!draft.permissionCategoryId) {
      toast.error("Permission category is required.");
      return;
    }
    setIsSaving(true);
    try {
      const payload: ManagedPermissionDraft = {
        name: draft.name.trim(),
        title: draft.title.trim(),
        permissionCategoryId: draft.permissionCategoryId,
      };
      if (editing) {
        const result = await updateManagedPermission(editing.id, payload);
        if (!result) {
          toast.error("Permission not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createManagedPermission(payload);
        toast.success(result.message);
      }
      closeModal();
      refresh();
    } catch (error) {
      console.error("[Permissions] Save failed", error);
      toast.error(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deleteManagedPermission(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Permissions] Delete failed", error);
      toast.error(errorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const canAdd = categories.length > 0;

  const content = (
    <Layout title="Permissions">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Permissions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage Spatie permission keys and display titles.{" "}
              <Link to="/permission-categories" className="text-primary hover:underline font-medium">
                Permission categories
              </Link>
              {" · "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          <Button variant="premium" className="gap-2" onClick={openCreate} disabled={(!canAdd && !isBootstrapping) || !canCreate}>
            <Plus className="w-4 h-4" />
            Add permission
          </Button>
        </div>

        {!canAdd && !isBootstrapping ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-900 dark:text-amber-100/90">
            Create at least one <Link to="/permission-categories" className="underline font-medium">permission category</Link> before adding permissions.
          </div>
        ) : null}

        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit permission" : "Add permission"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  className={cn("glass-input h-10", editing && "bg-muted/40 cursor-default")}
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="Name"
                  autoComplete="off"
                  readOnly={!!editing}
                  aria-readonly={!!editing || undefined}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  className="glass-input h-10"
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder="Title"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Permission category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={draft.permissionCategoryId || undefined}
                  onValueChange={(v) => setDraft((d) => ({ ...d, permissionCategoryId: v }))}
                >
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Guard</Label>
                <p className="text-sm text-foreground">web</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant="premium"
                onClick={() => void save()}
                disabled={isSaving || !canAdd || (editing ? !canUpdate : !canCreate)}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, title, or category…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 justify-between sm:justify-end">
              <GridPerPageSelect id="permissions-page-size" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="updated_latest">Recently updated</SelectItem>
                    <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading permissions…
          </div>
        ) : null}

        {!isBootstrapping && filtered.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0 ? "No permissions loaded yet, or none match your search." : "No permissions match your search."}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CatalogEntityCard
                  title={r.title}
                  primaryMeta={{
                    label: "Name",
                    value: r.name,
                    icon: KeyRound,
                    iconClassName: "text-amber-600",
                  }}
                  secondaryMeta={{
                    label: "Category",
                    value: r.categoryName ?? "—",
                    icon: Shield,
                    iconClassName: "text-violet-600",
                  }}
                  footer={`Guard ${r.guardName} · Updated ${formatCatalogAuditDate(r.updatedAt)}`}
                  onEdit={canUpdate ? () => openEdit(r) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(r) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="glass-card p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <KeyRound className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{r.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate font-mono">{r.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {r.categoryName ?? "Uncategorized"} · Updated {formatCatalogAuditDate(r.updatedAt)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Created {isValid(parseISO(r.createdAt)) ? format(parseISO(r.createdAt), "PP") : r.createdAt}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(r)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setDeleteTarget(r)}
                        aria-label="Delete permission"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this permission?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? Remove it from roles first if
                it is still in use.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );

  return isForbidden ? (
    <PermissionDenied
      title="Permissions access required"
      message="You don’t have permission to view managed permissions. Ask an administrator to grant managed_permission_can_view_any."
    />
  ) : (
    content
  );
};

export default Permissions;
